import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  NavBar, 
  Card, 
  Button, 
  Form, 
  Input, 
  Avatar, 
  Space,
  Toast,
  Dialog,
  Loading
} from 'antd-mobile';
import { 
  LeftOutline, 
  UserOutline, 
  EditSOutline, 
  CheckOutline,
  CloseOutline
} from 'antd-mobile-icons';
import { useI18n } from '@/hooks/useI18n';
import { getUserInfo, type User } from '@/lib/api/user';
import { updateUserInfo } from '@/lib/api/user';
import { isLoggedIn } from '@/lib/auth';
import { 
  useUserStore, 
  usePermissions, 
  useRoles, 
  useCorpInfo, 
  useEnvConfig, 
  useUserLoading 
} from '@/stores/userStore';

// 调试信息组件
const DebugInfoSection: React.FC = () => {
  const permissions = usePermissions();
  const roles = useRoles();
  const corpInfo = useCorpInfo();
  const envConfig = useEnvConfig();
  const loading = useUserLoading();
  const fetchUserPermissions = useUserStore(state => state.fetchUserPermissions);
  const { t } = useI18n();

  const handleRefresh = () => {
    fetchUserPermissions();
    Toast.show({
      icon: 'success',
      content: '正在刷新权限数据...'
    });
  };

  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, delay: 0.4 }}
      className="mt-4"
    >
      <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-800 flex items-center">
              <div className="w-1 h-6 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full mr-3" />
              调试信息 (Store数据)
            </h3>
            <Button
              size="mini"
              color="primary"
              fill="outline"
              onClick={handleRefresh}
              loading={loading}
              className="border-purple-200 text-purple-500 hover:bg-purple-50"
            >
              刷新
            </Button>
          </div>
          
          <Space direction="vertical" className="w-full gap-4">
            {/* 权限信息 */}
            <motion.div 
              whileHover={{ x: 4 }}
              className="p-4 bg-gradient-to-r from-blue-50/50 to-blue-100/50 rounded-xl"
            >
              <div className="text-gray-700 font-medium mb-2">权限列表 ({permissions.length}个)</div>
              <div className="text-sm text-gray-600 max-h-32 overflow-y-auto">
                {permissions.length > 0 ? (
                  <pre className="whitespace-pre-wrap text-xs">
                    {JSON.stringify(permissions, null, 2)}
                  </pre>
                ) : (
                  <span className="text-gray-400">暂无权限数据</span>
                )}
              </div>
            </motion.div>

            {/* 角色信息 */}
            <motion.div 
              whileHover={{ x: 4 }}
              className="p-4 bg-gradient-to-r from-green-50/50 to-green-100/50 rounded-xl"
            >
              <div className="text-gray-700 font-medium mb-2">角色列表 ({roles.length}个)</div>
              <div className="text-sm text-gray-600 max-h-32 overflow-y-auto">
                {roles.length > 0 ? (
                  <pre className="whitespace-pre-wrap text-xs">
                    {JSON.stringify(roles, null, 2)}
                  </pre>
                ) : (
                  <span className="text-gray-400">暂无角色数据</span>
                )}
              </div>
            </motion.div>

            {/* 企业信息 */}
            <motion.div 
              whileHover={{ x: 4 }}
              className="p-4 bg-gradient-to-r from-yellow-50/50 to-yellow-100/50 rounded-xl"
            >
              <div className="text-gray-700 font-medium mb-2">企业信息</div>
              <div className="text-sm text-gray-600 max-h-32 overflow-y-auto">
                {corpInfo ? (
                  <pre className="whitespace-pre-wrap text-xs">
                    {JSON.stringify(corpInfo, null, 2)}
                  </pre>
                ) : (
                  <span className="text-gray-400">暂无企业信息</span>
                )}
              </div>
            </motion.div>

            {/* 环境配置 */}
            <motion.div 
              whileHover={{ x: 4 }}
              className="p-4 bg-gradient-to-r from-purple-50/50 to-purple-100/50 rounded-xl"
            >
              <div className="text-gray-700 font-medium mb-2">环境配置</div>
              <div className="text-sm text-gray-600 max-h-32 overflow-y-auto">
                {envConfig ? (
                  <pre className="whitespace-pre-wrap text-xs">
                    {JSON.stringify(envConfig, null, 2)}
                  </pre>
                ) : (
                  <span className="text-gray-400">暂无环境配置</span>
                )}
              </div>
            </motion.div>

            {/* 加载状态 */}
            <motion.div 
              whileHover={{ x: 4 }}
              className="p-4 bg-gradient-to-r from-gray-50/50 to-gray-100/50 rounded-xl"
            >
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">加载状态</span>
                <span className={`font-semibold px-3 py-1 rounded-full text-sm ${
                  loading 
                    ? 'text-blue-600 bg-blue-100/80' 
                    : 'text-green-600 bg-green-100/80'
                }`}>
                  {loading ? '加载中...' : '已完成'}
                </span>
              </div>
            </motion.div>
          </Space>
        </div>
      </Card>
    </motion.div>
  );
};

export default function MyProfile() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/login');
      return;
    }
    fetchUserInfo();
  }, [navigate]);

  const fetchUserInfo = async () => {
    try {
      const response = await getUserInfo();
      if (response.success && response.data) {
        setUser(response.data);
        form.setFieldsValue({
          username: response.data.username,
          email: response.data.email,
          full_name: response.data.full_name || '',
          phone: response.data.phone || ''
        });
      }
    } catch (error) {
      console.error('获取用户信息失败:', error);
      Toast.show({
        icon: 'fail',
        content: t('profile.fetchError')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const values = await form.validateFields();
      
      const response = await updateUserInfo({
        full_name: values.full_name,
        phone: values.phone
      });
      
      if (response.success) {
        setUser(prev => prev ? { ...prev, ...values } : null);
        setEditing(false);
        Toast.show({
          icon: 'success',
          content: t('profile.updateSuccess')
        });
      } else {
        Toast.show({
          icon: 'fail',
          content: response.errorMessage || t('profile.updateError')
        });
      }
    } catch (error) {
      console.error('更新用户信息失败:', error);
      Toast.show({
        icon: 'fail',
        content: t('profile.updateError')
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      form.setFieldsValue({
        username: user.username,
        email: user.email,
        full_name: user.full_name || '',
        phone: user.phone || ''
      });
    }
    setEditing(false);
  };

  const confirmCancel = () => {
    Dialog.confirm({
      content: t('profile.cancelConfirm'),
      onConfirm: handleCancel
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loading />
          <p className="text-gray-500 mt-4">{t('common.loading')}</p>
        </motion.div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex justify-center">
      <div className="w-full min-h-screen relative overflow-hidden" style={{ maxWidth: '480px' }}>
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/20 to-pink-400/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-cyan-400/10 to-blue-400/10 rounded-full blur-3xl" />
      </div>

      {/* 顶部导航 */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <NavBar 
          onBack={() => navigate('/profile')}
          className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50"
          right={
            <div>
              {editing ? (
                <Space>
                  <Button
                    size="mini"
                    color="danger"
                    fill="outline"
                    onClick={confirmCancel}
                    disabled={saving}
                    className="border-red-200 text-red-500 hover:bg-red-50"
                  >
                    <CloseOutline />
                  </Button>
                  <Button
                    size="mini"
                    color="primary"
                    onClick={handleSave}
                    loading={saving}
                    className="bg-gradient-to-r from-blue-500 to-indigo-500 border-0 shadow-lg"
                  >
                    <CheckOutline />
                  </Button>
                </Space>
              ) : (
                <Button
                  size="mini"
                  color="primary"
                  fill="outline"
                  onClick={() => setEditing(true)}
                  className="border-blue-200 text-blue-500 hover:bg-blue-50"
                >
                  <EditSOutline />
                </Button>
              )}
            </div>
          }
        >
          <div className="flex items-center text-gray-800">
            <UserOutline className="mr-2" />
            {t('我的资料')}
          </div>
        </NavBar>
      </motion.div>

      <div className="p-4 relative z-10">
        {/* 头像区域 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="text-center py-8 px-4 bg-gradient-to-br from-blue-50/50 to-indigo-50/50">
              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Avatar
                  src={user.avatar || 'https://iknow-pic.cdn.bcebos.com/4ec2d5628535e5dd38dc075f64c6a7efcf1b62ba'}
                  fallback={user.username.charAt(0).toUpperCase()}
                  className="w-20 h-20 mx-auto mb-4 shadow-lg ring-4 ring-white/50"
                  style={{ '--size': '80px', backgroundColor: '#3b82f6' }}
                />
              </motion.div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {user.full_name || user.username}
              </h2>
              <p className="text-gray-500 text-sm">
                {t('profile.userId')}: {user.id}
              </p>
            </div>
          </Card>
        </motion.div>

        {/* 个人信息表单 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="mt-4"
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full mr-3" />
                {t('个人信息')}
              </h3>
              
              <Form form={form} layout="vertical">
                <Form.Item 
                  name="username" 
                  label={<span className="text-gray-700 font-medium">{t('profile.username')}</span>}
                  rules={[{ required: true, message: t('profile.usernameRequired') }]}
                >
                  <Input 
                    placeholder={t('profile.usernamePlaceholder')}
                    disabled={true}
                    className="bg-gray-50/80 border-gray-200/50 rounded-xl"
                  />
                </Form.Item>
                
                <Form.Item 
                  name="email" 
                  label={<span className="text-gray-700 font-medium">{t('profile.email')}</span>}
                  rules={[
                    { required: true, message: t('profile.emailRequired') },
                    { type: 'email', message: t('profile.emailInvalid') }
                  ]}
                >
                  <Input 
                    placeholder={t('profile.emailPlaceholder')}
                    disabled={true}
                    className="bg-gray-50/80 border-gray-200/50 rounded-xl"
                  />
                </Form.Item>
                
                <Form.Item 
                  name="full_name" 
                  label={<span className="text-gray-700 font-medium">{t('profile.fullName')}</span>}
                >
                  <Input 
                    placeholder={t('profile.fullNamePlaceholder')}
                    disabled={!editing}
                    className={`rounded-xl transition-all duration-200 ${
                      editing 
                        ? 'bg-white border-blue-200/50 focus:border-blue-400 focus:shadow-lg focus:shadow-blue-100' 
                        : 'bg-gray-50/80 border-gray-200/50'
                    }`}
                  />
                </Form.Item>
                
                <Form.Item 
                  name="phone" 
                  label={<span className="text-gray-700 font-medium">{t('profile.phone')}</span>}
                  rules={[
                    { pattern: /^1[3-9]\d{9}$/, message: t('profile.phoneInvalid') }
                  ]}
                >
                  <Input 
                    placeholder={t('profile.phonePlaceholder')}
                    disabled={!editing}
                    className={`rounded-xl transition-all duration-200 ${
                      editing 
                        ? 'bg-white border-blue-200/50 focus:border-blue-400 focus:shadow-lg focus:shadow-blue-100' 
                        : 'bg-gray-50/80 border-gray-200/50'
                    }`}
                  />
                </Form.Item>
              </Form>
            </div>
          </Card>
        </motion.div>

        {/* 账户信息 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mt-4"
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                <div className="w-1 h-6 bg-gradient-to-b from-green-500 to-emerald-500 rounded-full mr-3" />
                {t('账户信息')}
              </h3>
              
              <Space direction="vertical" className="w-full gap-4">
                <motion.div 
                  whileHover={{ x: 4 }}
                  className="flex justify-between items-center p-4 bg-gradient-to-r from-gray-50/50 to-gray-100/50 rounded-xl"
                >
                  <span className="text-gray-600 font-medium">{t('profile.accountStatus')}</span>
                  <span className={`font-semibold px-3 py-1 rounded-full text-sm ${
                    user.is_active 
                      ? 'text-green-600 bg-green-100/80' 
                      : 'text-red-600 bg-red-100/80'
                  }`}>
                    {user.is_active ? t('status.active') : t('status.disabled')}
                  </span>
                </motion.div>
                
                <motion.div 
                  whileHover={{ x: 4 }}
                  className="flex justify-between items-center p-4 bg-gradient-to-r from-gray-50/50 to-gray-100/50 rounded-xl"
                >
                  <span className="text-gray-600 font-medium">{t('profile.adminPermission')}</span>
                  <span className={`font-semibold px-3 py-1 rounded-full text-sm ${
                    user.is_superuser 
                      ? 'text-amber-600 bg-amber-100/80' 
                      : 'text-gray-600 bg-gray-100/80'
                  }`}>
                    {user.is_superuser ? t('status.yes') : t('status.no')}
                  </span>
                </motion.div>
                
                <motion.div 
                  whileHover={{ x: 4 }}
                  className="flex justify-between items-center p-4 bg-gradient-to-r from-gray-50/50 to-gray-100/50 rounded-xl"
                >
                  <span className="text-gray-600 font-medium">{t('profile.createdAt')}</span>
                  <span className="text-gray-700 font-medium">
                    {new Date(user.created_at).toLocaleDateString('zh-CN')}
                  </span>
                </motion.div>
              </Space>
            </div>
          </Card>
        </motion.div>

        {/* 调试信息 */}
        {/* <DebugInfoSection /> */}

        {/* 操作按钮 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.5 }}
          className="mt-6"
        >
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Button
              color="primary"
              block
              onClick={() => navigate('/profile')}
              className="bg-gradient-to-r from-blue-500 to-indigo-500 border-0 shadow-xl rounded-2xl h-14 text-lg font-semibold"
            >
              {t('返回')}
            </Button>
          </motion.div>
        </motion.div>

        {/* 底部间距 */}
        <div className="h-8" />
      </div>
      </div>
    </div>
  );
}